// Shared friendly-error mapping for AI gateway responses.
//
// Catch Me Up, AI Drafting, and Smart Search all talk to the same AI
// gateway and can surface the same set of error codes
// (AI_DISABLED, AI_BUDGET_EXCEEDED, AI_PARSE_ERROR, ...). We centralise
// the code -> human-friendly message mapping here so every surface stays
// in sync and admins consistently get a link to AI billing/settings.

export type AiErrorCode =
  | "AI_CATCH_ME_UP_DISABLED"
  | "AI_DISABLED"
  | "AI_INSUFFICIENT_HISTORY"
  | "AI_BUDGET_EXCEEDED"
  | "AI_PARSE_ERROR"
  | "AI_PROVIDER_ERROR"
  | "AI_ERROR";

export interface FriendlyAiError {
  /** Short title for the toast/inline header. */
  title: string;
  /** Plain-language explanation suitable for both toast and inline display. */
  message: string;
  /** Optional extra hint shown only inline. */
  hint?: string;
  /** Optional in-app action (e.g. "Open AI billing"). */
  action?: { label: string; href: string };
}

export interface DescribeAiErrorOptions {
  isAdmin: boolean;
  /** Title shown for unrecognised codes (e.g. "Catch me up failed"). */
  defaultTitle: string;
  /** Fallback message used when the server didn't send one. */
  defaultMessage?: string;
}

/**
 * Best-effort parser for errors thrown by `apiRequest`. Those errors are
 * formatted as `${status}: ${responseText}` where the body is usually JSON
 * like `{ message, code, ... }`. We slice from the first `{` and try to
 * parse it. If anything fails, we fall back to the raw message.
 */
export function parseApiError(err: unknown): { code?: string; message?: string; status?: number } {
  const raw = err instanceof Error ? err.message : String(err ?? "");
  // Pull the leading "<status>:" prefix if `apiRequest` formatted it.
  const statusMatch = raw.match(/^(\d{3}):\s/);
  const status = statusMatch ? Number(statusMatch[1]) : undefined;
  const braceStart = raw.indexOf("{");
  if (braceStart === -1) return { message: raw, status };
  try {
    const parsed = JSON.parse(raw.slice(braceStart));
    return {
      code: typeof parsed?.code === "string" ? parsed.code : undefined,
      message: typeof parsed?.message === "string" ? parsed.message : raw,
      status,
    };
  } catch {
    return { message: raw, status };
  }
}

/**
 * Convert a server-side AI error code into a tailored, human-friendly
 * message. Different codes map to very different recovery paths
 * (admin needs to flip a switch, user needs to log more activity,
 * billing cap was hit, etc.) so we surface them distinctly instead of
 * one generic toast.
 */
export function describeAiError(
  code: string | undefined,
  serverMessage: string | undefined,
  opts: DescribeAiErrorOptions,
): FriendlyAiError {
  const { isAdmin, defaultTitle, defaultMessage } = opts;
  switch (code as AiErrorCode | undefined) {
    case "AI_CATCH_ME_UP_DISABLED":
      return {
        title: "Catch me up is off",
        message: isAdmin
          ? "Catch me up is turned off for this workspace. Turn it back on under AI assist."
          : "An admin has turned Catch me up off for this workspace.",
        action: isAdmin
          ? { label: "Open AI assist settings", href: "/admin?section=ai" }
          : undefined,
      };
    case "AI_DISABLED":
      return {
        title: "AI assist is off",
        message: isAdmin
          ? "AI assist is disabled for this workspace. Re-enable it under AI assist."
          : "AI assist is disabled for this workspace. Ask an admin to turn it on.",
        action: isAdmin
          ? { label: "Open AI assist settings", href: "/admin?section=ai" }
          : undefined,
      };
    case "AI_INSUFFICIENT_HISTORY":
      return {
        title: "Not enough activity yet",
        message: "There isn't enough logged activity to summarize.",
        hint: "Catch me up needs at least 2 logged events (notes, calls, emails, invoice updates, etc.).",
      };
    case "AI_BUDGET_EXCEEDED":
      return {
        title: "Out of AI credits",
        message: isAdmin
          ? "You've hit your AI credit cap for this month. Buy more credits or raise the overage cap to keep going."
          : "Your workspace has hit its AI credit cap for this month. Ask an admin to raise the cap or add credits.",
        action: isAdmin
          ? { label: "Open AI billing", href: "/admin?section=ai" }
          : undefined,
      };
    case "AI_PARSE_ERROR":
      return {
        title: "Couldn't interpret that",
        message:
          serverMessage ||
          "The AI couldn't turn that into a usable result. Try rephrasing or simplifying the request.",
      };
    case "AI_PROVIDER_ERROR":
      return {
        title: defaultTitle,
        message:
          serverMessage ||
          "The AI provider had a hiccup. Try again in a moment — if it keeps happening, let an admin know.",
      };
    default:
      return {
        title: defaultTitle,
        message: serverMessage || defaultMessage || "Something went wrong while talking to the AI.",
      };
  }
}
